import { Button } from "primereact/button";
import { motion } from "framer-motion";
import * as client from "../../client";
import styles from "./Comment.module.css";
import { Badge } from "primereact/badge";
import { OverlayPanel } from "primereact/overlaypanel";
import { useRef, useState } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import {
  useAddComment,
  useCommentsForReport,
  useDeleteComment,
  useEditComment,
  useLocalUser,
  useUsers,
} from "../../pages/specialty-assistant/data";
import { PantheonAvatarWithoutMenu } from "../avatar/Avatar";
import { displayNameForUser } from "../../util";
import React from "react";
import { confirmDialog } from "primereact/confirmdialog";

type CommentContainerProps = {
  reportName: string;
  reportId: string;
  fetchEnabled?: boolean;
};

const isEdited = (comment: client.Comment): boolean => {
  return comment.last_updated_ts !== comment.created_at_ts;
};

export const CommentContainer: React.FC<CommentContainerProps> = ({
  reportName,
  reportId,
  fetchEnabled = true,
}) => {
  const containerVariants = {
    rest: {},
    hover: { scale: 1.05, boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.2)" },
  };

  const overlayPanel = useRef<OverlayPanel>(null);
  const [newComment, setNewComment] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const commentsQuery = useCommentsForReport(reportId, fetchEnabled);
  const allComments: client.Comment[] = commentsQuery.data?.all_comments ?? [];
  const addComment = useAddComment(reportId);
  const deleteCommentMutation = useDeleteComment(reportId);
  const editCommentMutation = useEditComment(reportId);
  const users = useUsers();
  const localUser = useLocalUser();

  const onAddComment = async () => {
    if (newComment) {
      await addComment.mutateAsync(newComment);
      setNewComment("");
    }
  };

  const onDelete = (commentId: string) => {
    confirmDialog({
      message: "Are you sure you want to delete this comment?",
      header: "Delete Comment",
      icon: "pi pi-trash",
      acceptClassName: "p-button-danger",
      accept: () => deleteCommentMutation.mutate(commentId),
    });
  };

  const onStartEdit = (comment: client.Comment) => {
    setEditingId(comment.id);
    setEditText(comment.message);
  };

  const onSaveEdit = async () => {
    if (editingId && editText) {
      await editCommentMutation.mutateAsync({
        commentId: editingId,
        message: editText,
      });
      setEditingId(null);
      setEditText("");
    }
  };

  const onCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const isOwnComment = (comment: client.Comment) =>
    localUser?.id === comment.user_id;

  return (
    <motion.div
      variants={containerVariants}
      className={styles.buttonContainer}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.2 }}
      initial="rest"
      whileHover="hover"
      style={allComments.length === 0 ? { width: "28px" } : {}}
    >
      <Button
        className={styles.mainButton}
        icon="pi pi-comment"
        size="small"
        severity="secondary"
        text
        onClick={(e) => {
          if (overlayPanel.current) {
            overlayPanel.current.toggle(e);
          }
        }}
      >
        {allComments.length > 0 && (
          <Badge value={allComments.length} severity="info" />
        )}
        <OverlayPanel
          ref={overlayPanel}
          showCloseIcon
          style={{
            marginTop: "20px",
            marginBottom: "20px",
            marginLeft: "20px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.commentListContainer}>
            <div
              style={{
                fontWeight: "bold",
                fontSize: "18px",
                marginBottom: "12px",
              }}
            >
              {`${reportName} Comments`}
            </div>
            {allComments.map((comment) => {
              const user = users.data?.all_company_users.find(
                (user) => user.id === comment.user_id
              );
              const postTime = new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "numeric",
              }).format(new Date(comment.created_at_ts));
              const editing = editingId === comment.id;

              return (
                <div key={comment.id} className={styles.commentRow}>
                  <div className={styles.horizontalContainer}>
                    <PantheonAvatarWithoutMenu
                      label={displayNameForUser(user)}
                      showName={true}
                    />
                    <div className={styles.timeText}>
                      {postTime}
                      {isEdited(comment) && (
                        <span className={styles.editedIndicator}>
                          {" "}
                          (edited)
                        </span>
                      )}
                    </div>
                    {isOwnComment(comment) && !editing && (
                      <div className={styles.commentActions}>
                        <Button
                          icon="pi pi-pencil"
                          text
                          severity="secondary"
                          size="small"
                          className={styles.actionButton}
                          onClick={() => onStartEdit(comment)}
                          tooltip="Edit"
                          tooltipOptions={{ position: "top" }}
                        />
                        <Button
                          icon="pi pi-trash"
                          text
                          severity="danger"
                          size="small"
                          className={styles.actionButton}
                          onClick={() => onDelete(comment.id)}
                          tooltip="Delete"
                          tooltipOptions={{ position: "top" }}
                        />
                      </div>
                    )}
                  </div>
                  {editing ? (
                    <div className={styles.editContainer}>
                      <InputTextarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            editText &&
                            e.key === "Enter" &&
                            (e.shiftKey || e.metaKey || e.ctrlKey)
                          ) {
                            onSaveEdit();
                          }
                          if (e.key === "Escape") {
                            onCancelEdit();
                          }
                        }}
                        autoResize
                        style={{ width: "100%" }}
                        disabled={editCommentMutation.isPending}
                      />
                      <div className={styles.editActions}>
                        <Button
                          label="Cancel"
                          text
                          size="small"
                          severity="secondary"
                          onClick={onCancelEdit}
                          disabled={editCommentMutation.isPending}
                        />
                        <Button
                          label="Save"
                          size="small"
                          onClick={onSaveEdit}
                          loading={editCommentMutation.isPending}
                          disabled={!editText || editCommentMutation.isPending}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className={styles.commentMessage}>
                      {comment.message}
                    </div>
                  )}
                </div>
              );
            })}
            <div className={styles.horizontalContainer}>
              <PantheonAvatarWithoutMenu
                label={displayNameForUser(localUser)}
              />
              <InputTextarea
                placeholder="Add a comment..."
                rows={1}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    newComment &&
                    e.key == "Enter" &&
                    (e.shiftKey || e.metaKey || e.ctrlKey)
                  ) {
                    onAddComment();
                  }
                }}
                value={newComment}
                style={{ flexGrow: "1", margin: "2px" }}
                disabled={addComment.isPending}
                autoResize
              />
              <Button
                loading={addComment.isPending}
                disabled={!newComment || addComment.isPending}
                icon="pi pi-arrow-circle-up"
                onClick={onAddComment}
                style={{ height: "28px", width: "28px" }}
              />
            </div>
          </div>
        </OverlayPanel>
      </Button>
    </motion.div>
  );
};
